import {createStore} from 'zustand';

export type DashboardSearchBy = 'id' | 'extNumber' | 'deviceNumberNew';
export type DashboardFilter = [string, string];

export interface DashboardServiceRequestsFilterSet {
  id: string;
  name: string;
  searchBy: DashboardSearchBy;
  searchValue: string;
  filters: DashboardFilter[];
}

interface PersistedState {
  searchBy: DashboardSearchBy;
  searchValue: string;
  filters: DashboardFilter[];
  filterSets: DashboardServiceRequestsFilterSet[];
  currentFilterSetId: string | null;
}

export interface DashboardServiceRequestsFiltersStore extends PersistedState {
  setSearchBy: (searchBy: DashboardSearchBy) => void;
  setSearchValue: (searchValue: string) => void;
  addFilter: (filter: DashboardFilter) => void;
  removeFilter: (filterBy: string) => void;
  resetAll: () => void;
  saveCurrentFilterSet: (name: string) => void;
  applyFilterSet: (id: string) => void;
  deleteFilterSet: (id: string) => void;
}

const STORAGE_KEY_PREFIX = 'dashboard-service-requests-filters';
export const DASHBOARD_SERVICE_REQUEST_FILTER_KEYS = new Set([
  'contractNumber',
  'status',
  'taskLocationCode',
  'workerUid',
  'fullAddress',
]);

const DEFAULT_STATE: PersistedState = {
  searchBy: 'id',
  searchValue: '',
  filters: [],
  filterSets: [],
  currentFilterSetId: null,
};

const getStorageKey = (serviceType: string) => `${STORAGE_KEY_PREFIX}:${serviceType}`;

const sanitizeFilters = (filters: unknown): DashboardFilter[] =>
  Array.isArray(filters)
    ? filters.filter(
        (filter): filter is DashboardFilter =>
          Array.isArray(filter) &&
          filter.length === 2 &&
          typeof filter[0] === 'string' &&
          typeof filter[1] === 'string' &&
          DASHBOARD_SERVICE_REQUEST_FILTER_KEYS.has(filter[0]),
      )
    : DEFAULT_STATE.filters;

const loadState = (serviceType: string): PersistedState => {
  try {
    const rawValue = localStorage.getItem(getStorageKey(serviceType));

    if (!rawValue) {
      return DEFAULT_STATE;
    }

    const parsed = JSON.parse(rawValue) as Partial<PersistedState>;
    const filterSets = Array.isArray(parsed.filterSets)
      ? parsed.filterSets.map((filterSet) => ({
          ...filterSet,
          filters: sanitizeFilters(filterSet.filters),
        }))
      : DEFAULT_STATE.filterSets;

    return {
      searchBy: parsed.searchBy ?? DEFAULT_STATE.searchBy,
      searchValue: parsed.searchValue ?? DEFAULT_STATE.searchValue,
      filters: sanitizeFilters(parsed.filters),
      filterSets,
      currentFilterSetId: parsed.currentFilterSetId ?? DEFAULT_STATE.currentFilterSetId,
    };
  } catch {
    return DEFAULT_STATE;
  }
};

const persistState = (serviceType: string, state: PersistedState) => {
  try {
    localStorage.setItem(getStorageKey(serviceType), JSON.stringify(state));
  } catch {
    // ignore storage write failures
  }
};

const createFilterSetId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const createDashboardServiceRequestsFiltersStore = (serviceType: string) => {
  const persistedState = loadState(serviceType);
  persistState(serviceType, persistedState);

  return createStore<DashboardServiceRequestsFiltersStore>((set, get) => ({
    ...persistedState,
    setSearchBy: (searchBy) => {
      const nextState = {
        ...get(),
        searchBy,
        currentFilterSetId: null,
      };

      persistState(serviceType, nextState);
      set({searchBy, currentFilterSetId: null});
    },
    setSearchValue: (searchValue) => {
      const nextState = {
        ...get(),
        searchValue,
        currentFilterSetId: null,
      };

      persistState(serviceType, nextState);
      set({searchValue, currentFilterSetId: null});
    },
    addFilter: (filter) => {
      const nextFilters = [...get().filters.filter((item) => item[0] !== filter[0]), filter];
      const nextState = {
        ...get(),
        filters: nextFilters,
        currentFilterSetId: null,
      };

      persistState(serviceType, nextState);
      set({filters: nextFilters, currentFilterSetId: null});
    },
    removeFilter: (filterBy) => {
      const nextFilters = get().filters.filter((item) => item[0] !== filterBy);
      const nextState = {
        ...get(),
        filters: nextFilters,
        currentFilterSetId: null,
      };

      persistState(serviceType, nextState);
      set({filters: nextFilters, currentFilterSetId: null});
    },
    resetAll: () => {
      const nextState = {
        ...get(),
        searchBy: DEFAULT_STATE.searchBy,
        searchValue: DEFAULT_STATE.searchValue,
        filters: DEFAULT_STATE.filters,
        currentFilterSetId: null,
      };

      persistState(serviceType, nextState);
      set({
        searchBy: DEFAULT_STATE.searchBy,
        searchValue: DEFAULT_STATE.searchValue,
        filters: DEFAULT_STATE.filters,
        currentFilterSetId: null,
      });
    },
    saveCurrentFilterSet: (name) => {
      const trimmedName = name.trim();

      if (!trimmedName) {
        return;
      }

      const currentState = get();
      const nextSet: DashboardServiceRequestsFilterSet = {
        id: createFilterSetId(),
        name: trimmedName,
        searchBy: currentState.searchBy,
        searchValue: currentState.searchValue,
        filters: currentState.filters,
      };
      const nextFilterSets = [...currentState.filterSets.filter((item) => item.name !== trimmedName), nextSet];
      const nextState = {
        ...currentState,
        filterSets: nextFilterSets,
        currentFilterSetId: nextSet.id,
      };

      persistState(serviceType, nextState);
      set({
        filterSets: nextFilterSets,
        currentFilterSetId: nextSet.id,
      });
    },
    applyFilterSet: (id) => {
      const selectedSet = get().filterSets.find((item) => item.id === id);

      if (!selectedSet) {
        return;
      }

      const nextState = {
        ...get(),
        searchBy: selectedSet.searchBy,
        searchValue: selectedSet.searchValue,
        filters: selectedSet.filters,
        currentFilterSetId: selectedSet.id,
      };

      persistState(serviceType, nextState);
      set({
        searchBy: selectedSet.searchBy,
        searchValue: selectedSet.searchValue,
        filters: selectedSet.filters,
        currentFilterSetId: selectedSet.id,
      });
    },
    deleteFilterSet: (id) => {
      const nextFilterSets = get().filterSets.filter((item) => item.id !== id);
      const nextCurrentFilterSetId = get().currentFilterSetId === id ? null : get().currentFilterSetId;
      const nextState = {
        ...get(),
        filterSets: nextFilterSets,
        currentFilterSetId: nextCurrentFilterSetId,
      };

      persistState(serviceType, nextState);
      set({
        filterSets: nextFilterSets,
        currentFilterSetId: nextCurrentFilterSetId,
      });
    },
  }));
};
